import { useRef, useState, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Search, BadgeCheck } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import StoreInfo from "@/components/StoreInfo";
import { CATEGORIES, PRODUCTS, HERO_IMAGE, CONTACT } from "@/data/products";

const lineAnim = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CHAPTERS = [
  {
    num: "01",
    title: "Direct Sourcing, Trusted Farms",
    hindi: "सीधा संबंध, साफ़ सामान",
    body: "Grains, dals and spices come straight from mandis and mills we have worked with for decades. No middlemen games — just honest stock on honest shelves.",
  },
  {
    num: "02",
    title: "Uncompromising Purity",
    hindi: "शुद्धता पर कोई समझौता नहीं",
    body: "Every sack is checked, every batch weighed fair. If it is not good enough for our own kitchen, it never reaches yours.",
  },
  {
    num: "03",
    title: "30+ Years of Neighbourhood Trust",
    hindi: "तीस साल का विश्वास",
    body: "Generations of families have filled their kitchens from our shelves. We know your usual, we keep your favourites, and we deliver to your door.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(
    () =>
      PRODUCTS.filter(
        (p) =>
          (cat === "all" || p.category === cat) &&
          (p.name + p.hindi).toLowerCase().includes(query.toLowerCase())
      ),
    [cat, query]
  );

  return (
    <main>
      {/* KINETIC HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden grain spotlight pt-24 pb-16 px-6 md:px-10">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="overflow-hidden">
              <motion.p
                custom={0}
                variants={lineAnim}
                initial="hidden"
                animate="visible"
                data-testid="hero-tagline-hindi"
                className="font-hindi text-spice text-xl md:text-2xl"
              >
                आपकी अपनी दुकान
              </motion.p>
            </div>
            <h1 data-testid="hero-title" className="font-serif font-bold tracking-tight text-ink mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              <span className="block overflow-hidden">
                <motion.span custom={1} variants={lineAnim} initial="hidden" animate="visible" className="block">
                  Raj Kumari
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span custom={2} variants={lineAnim} initial="hidden" animate="visible" className="block italic text-spice">
                  Kirana Store
                </motion.span>
              </span>
            </h1>
            <div className="overflow-hidden mt-6 max-w-md">
              <motion.p custom={3} variants={lineAnim} initial="hidden" animate="visible" className="text-bark text-base md:text-lg leading-relaxed">
                Daily essentials, pure spices and festive needs — handpicked every morning, priced fairly, delivered to your doorstep.
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap gap-4 mt-9"
            >
              <a
                data-testid="hero-whatsapp-btn"
                href={`${CONTACT.whatsapp}?text=${encodeURIComponent("Namaste! I would like to place an order.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-ink text-cream font-semibold text-sm px-7 py-4 rounded-full hover:bg-spice transition-colors duration-300"
              >
                <MessageCircle size={16} /> Order on WhatsApp
              </a>
              <button
                data-testid="hero-explore-btn"
                onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 border border-ink/25 text-ink font-semibold text-sm px-7 py-4 rounded-full hover:border-spice hover:text-spice transition-colors duration-300"
              >
                Explore the Shelves <ArrowDown size={16} />
              </button>
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} className="relative hidden lg:block">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.5, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-t-[999px] rounded-b-3xl overflow-hidden border border-linen shadow-2xl shadow-ink/20 aspect-[4/5] max-w-md ml-auto"
            >
              <img src={HERO_IMAGE} alt="Fresh Indian spices and grains at Raj Kumari Kirana Store" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-6 -left-2 bg-cream border border-linen rounded-2xl px-6 py-4 shadow-xl shadow-ink/10 flex items-center gap-3"
            >
              <BadgeCheck className="text-forest" size={26} />
              <div>
                <p className="font-serif font-bold text-ink text-sm">30+ Years of Trust</p>
                <p className="text-mut text-xs">शुद्धता और विश्वास</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section id="manifesto" className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="uppercase text-xs sm:text-sm font-semibold tracking-[0.2em] text-spice">Our Manifesto</p>
            <h2 className="font-serif font-bold tracking-tight text-cocoa text-3xl md:text-5xl mt-3 max-w-2xl">
              Three promises, kept every single day.
            </h2>
          </Reveal>
          <div className="mt-16 flex flex-col">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div className="group grid grid-cols-1 md:grid-cols-[120px_1fr_1.2fr] gap-6 md:gap-10 items-baseline py-10 border-t border-linen last:border-b hover:bg-sand/60 transition-colors duration-500 px-2 md:px-6 -mx-2 md:-mx-6 rounded-lg">
                  <span className="font-serif italic text-5xl md:text-6xl text-stroke group-hover:text-spice group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                    {c.num}
                  </span>
                  <div>
                    <h3 className="font-serif font-semibold text-coffee text-2xl md:text-3xl">{c.title}</h3>
                    <p className="font-hindi text-spice mt-2">{c.hindi}</p>
                  </div>
                  <p className="text-bark leading-relaxed text-sm md:text-base">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section id="products" className="bg-sand px-6 md:px-10 py-24 md:py-32 grain relative">
        <div className="max-w-7xl mx-auto">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <p className="uppercase text-xs sm:text-sm font-semibold tracking-[0.2em] text-spice">The Shelves</p>
              <h2 className="font-serif font-bold tracking-tight text-cocoa text-3xl md:text-5xl mt-3">
                किराना और राशन, सब कुछ।
              </h2>
            </div>
            <div className="relative w-full md:w-72">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mut" />
              <input
                data-testid="product-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the shelves…"
                className="w-full bg-white border border-linen rounded-full pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-spice focus:ring-2 focus:ring-spice/20 transition-all"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="flex flex-wrap gap-2.5 mt-10">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={c.testid}
                onClick={() => setCat(c.id)}
                className={`text-xs md:text-sm font-semibold px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  cat === c.id
                    ? "bg-ink text-cream border-ink"
                    : "bg-white/60 text-bark border-linen hover:border-spice hover:text-spice"
                }`}
              >
                {c.label}
              </button>
            ))}
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {filtered.map((p, i) => (
              <motion.article
                key={p.name}
                data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-white border border-linen rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-ink/10 hover:-translate-y-1.5 transition-all duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  <span className="absolute top-3 left-3 bg-cream/90 backdrop-blur text-ink text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                    {CATEGORIES.find((c) => c.id === p.category)?.label}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif font-semibold text-coffee text-lg">{p.name}</h3>
                  <p className="font-hindi text-spice text-sm mt-0.5">{p.hindi}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-ink font-bold text-sm">{p.price}</span>
                    <a
                      data-testid="product-card-enquire-btn"
                      href={`${CONTACT.whatsapp}?text=${encodeURIComponent(`Namaste! I would like to enquire about ${p.name} (${p.price}).`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest border border-forest/30 px-4 py-2 rounded-full hover:bg-forest hover:text-cream transition-colors duration-300"
                    >
                      <MessageCircle size={13} /> Enquire
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-mut mt-16 font-serif italic text-xl">
              Nothing on that shelf yet — ask us on WhatsApp, we probably have it in store.
            </p>
          )}
        </div>
      </section>

      {/* VISIT + CONTACT */}
      <section id="visit" className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="uppercase text-xs sm:text-sm font-semibold tracking-[0.2em] text-spice">Say Namaste</p>
            <h2 className="font-serif font-bold tracking-tight text-cocoa text-3xl md:text-5xl mt-3">
              Bulk order, monthly ration, or just a question?
            </h2>
            <p className="text-bark mt-5 max-w-md leading-relaxed">
              Drop your list here and we will call you back with prices — or ping us straight on WhatsApp for the fastest reply.
            </p>
            <div className="mt-10">
              <StoreInfo />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="bg-white border border-linen rounded-3xl p-8 md:p-10 shadow-sm">
            <h3 className="font-serif font-semibold text-coffee text-2xl mb-6">Send an Enquiry</h3>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
